import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

import Nav from './nav';
import { VishingCallModal } from './vishingCallModal';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import { resolveMaxRedials } from '../pentests/vishing/constants';

const PASS_GREEN = '#2E7D32';
const FAIL_RED = '#C62828';
const SLATE_TEXT = '#607086';

const QUICK_FILTERS = [
  { key: 'all', label: 'All Calls' },
  { key: 'recorded', label: 'Recorded' },
  { key: 'failed', label: 'Failed' },
  { key: 'passed', label: 'Passed' },
  { key: 'ongoing', label: 'Ongoing' }
];

const COUNTRY_PREFIXES = [
  { code: '90', name: 'Turkey', flag: 'TR' },
  { code: '1', name: 'United States/Canada', flag: 'US' },
  { code: '44', name: 'United Kingdom', flag: 'GB' },
  { code: '33', name: 'France', flag: 'FR' },
  { code: '49', name: 'Germany', flag: 'DE' },
  { code: '34', name: 'Spain', flag: 'ES' },
  { code: '39', name: 'Italy', flag: 'IT' },
  { code: '91', name: 'India', flag: 'IN' },
  { code: '81', name: 'Japan', flag: 'JP' },
  { code: '86', name: 'China', flag: 'CN' }
];

const isFailureResult = (value) => {
  if (value === undefined || value === null || value === '') return null;
  return typeof value === 'string' ? value.toLowerCase() === 'true' : !!value;
};

const isOngoingStatus = (status) => {
  const value = String(status || '').toLowerCase();
  return value.includes('in') || value.includes('ring') || value.includes('ongo');
};

const statusColor = (status) => {
  if (!status) return 'default';
  const value = status.toLowerCase();
  if (value.includes('in') || value.includes('ring') || value.includes('ongo')) return 'info';
  if (value.includes('answer') || value.includes('miss')) return 'warning';
  if (value.includes('fail') || value.includes('error')) return 'error';
  return 'success';
};

const passFailChip = (value) => {
  const failed = isFailureResult(value);
  if (failed === null) {
    return <Chip size="small" label="No Result" variant="outlined" sx={{ fontWeight: 700 }} />;
  }

  return (
    <Chip
      size="small"
      label={failed ? 'Failed' : 'Passed'}
      sx={{
        fontWeight: 800,
        bgcolor: failed ? alpha(FAIL_RED, 0.1) : alpha(PASS_GREEN, 0.1),
        color: failed ? FAIL_RED : PASS_GREEN
      }}
    />
  );
};

const getCountryFromPhone = (phone) => {
  if (!phone) return { flag: 'ZZ', name: 'Unknown' };
  const digits = phone.replace(/[^\d]/g, '');
  const number = digits.startsWith('00') ? digits.slice(2) : digits;
  let match = null;

  for (const entry of COUNTRY_PREFIXES.sort((a, b) => b.code.length - a.code.length)) {
    if (number.startsWith(entry.code)) {
      match = entry;
      break;
    }
  }

  return match || { flag: 'ZZ', name: 'Unknown' };
};

const getInitials = (value) => {
  if (!value) return '?';
  return String(value)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || '?';
};

const cellLabelSX = { fontSize: 12, color: SLATE_TEXT };

const MetricCard = ({ label, value, helper, Icon, accent }) => (
  <Card
    sx={{
      height: '100%',
      borderRadius: 4,
      border: '1px solid',
      borderColor: alpha(PRIMARYCOLOR, 0.1),
      boxShadow: `0 12px 24px ${alpha(PRIMARYCOLOR, 0.05)}`
    }}
  >
    <CardContent sx={{ p: 2.1 }}>
      <Stack direction="row" spacing={1.4} alignItems="flex-start">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 3,
            bgcolor: alpha(accent, 0.12),
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Icon />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 800, color: '#10233B', lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.85, color: SLATE_TEXT, lineHeight: 1.65 }}>
            {helper}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const VishingCalls = () => {
  const [callsData, setCallsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('name');
  const [quickFilter, setQuickFilter] = useState('all');
  const [openModal, setOpenModal] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [voiceMap, setVoiceMap] = useState({});
  const [scenarioMap, setScenarioMap] = useState({});

  const navigate = useNavigate();
  const { pentest_id } = useParams();

  const getCalls = useCallback(async (token) => {
    const response = await fetch(`${BASEURL}/pentests/vishing/users?pentest_id=${pentest_id}`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    });
    const data = await response.json();
    if (data?.error && data?.message === 'Invalid token') {
      navigate('/');
      return;
    }
    setCallsData(Array.isArray(data?.users) ? data.users : []);
  }, [navigate, pentest_id]);

  const getVoicesScenarios = useCallback(async (token) => {
    const response = await fetch(`${BASEURL}/pentests/vishing/voices_scenrios`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    });
    const data = await response.json();
    const nextVoiceMap = {};
    const nextScenarioMap = {};

    (data?.info?.voices || []).forEach((voice) => {
      nextVoiceMap[String(voice.id)] = voice;
    });
    (data?.info?.scenarios || []).forEach((scenario) => {
      nextScenarioMap[String(scenario.id)] = scenario;
    });

    setVoiceMap(nextVoiceMap);
    setScenarioMap(nextScenarioMap);
  }, []);

  useEffect(() => {
    (async () => {
      const token = sessionStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      try {
        setLoading(true);
        await Promise.all([getCalls(token), getVoicesScenarios(token)]);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [getCalls, getVoicesScenarios, navigate]);

  const stats = useMemo(() => {
    const total = callsData.length;
    const recorded = callsData.filter((call) => Boolean(call?.recording)).length;
    const failed = callsData.filter((call) => isFailureResult(call?.passOrFail) === true).length;
    const passed = callsData.filter((call) => isFailureResult(call?.passOrFail) === false).length;
    const ongoing = callsData.filter((call) => isOngoingStatus(call?.status)).length;

    return { total, recorded, failed, passed, ongoing };
  }, [callsData]);

  const filteredCalls = useMemo(() => {
    return callsData.filter((call) => {
      const failed = isFailureResult(call?.passOrFail);

      if (quickFilter === 'recorded' && !call?.recording) return false;
      if (quickFilter === 'failed' && failed !== true) return false;
      if (quickFilter === 'passed' && failed !== false) return false;
      if (quickFilter === 'ongoing' && !isOngoingStatus(call?.status)) return false;

      if (!searchTerm) return true;

      const query = searchTerm.toLowerCase();
      if (searchCategory === 'name') return (call.name || '').toLowerCase().includes(query);
      if (searchCategory === 'phone') return (call.phone || '').toLowerCase().includes(query);
      if (searchCategory === 'status') return (call.status || '').toLowerCase().includes(query);
      if (searchCategory === 'result') {
        const label = failed === null ? 'no result' : failed ? 'failed' : 'passed';
        return label.includes(query);
      }

      return true;
    });
  }, [callsData, quickFilter, searchCategory, searchTerm]);

  const handleViewMore = (call) => {
    const voiceId = call?.voice ? String(call.voice) : null;
    const scenarioId = call?.scenario ? String(call.scenario) : null;

    setSelectedCall({
      ...call,
      voiceInfo: voiceId && voiceMap[voiceId] ? voiceMap[voiceId] : null,
      scenarioInfo: scenarioId && scenarioMap[scenarioId] ? scenarioMap[scenarioId] : null
    });
    setOpenModal(true);
  };

  const metricCards = [
    {
      label: 'Total Calls',
      value: stats.total,
      helper: 'All campaign call records currently available for review.',
      Icon: PhoneIphoneIcon,
      accent: PRIMARYCOLOR
    },
    {
      label: 'Recorded Calls',
      value: stats.recorded,
      helper: 'Calls with audio available for playback in the review modal.',
      Icon: GraphicEqRoundedIcon,
      accent: PRIMARYCOLOR
    },
    {
      label: 'Failed Users',
      value: stats.failed,
      helper: 'Calls where the user was successfully influenced by the pretext.',
      Icon: ErrorOutlineRoundedIcon,
      accent: FAIL_RED
    },
    {
      label: 'Passed Users',
      value: stats.passed,
      helper: 'Calls where the user resisted the social engineering attempt.',
      Icon: CheckCircleOutlineRoundedIcon,
      accent: PASS_GREEN
    },
    {
      label: 'Ongoing Calls',
      value: stats.ongoing,
      helper: 'Calls that still appear to be active or not yet fully resolved.',
      Icon: PendingOutlinedIcon,
      accent: '#D97706'
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F5F8FC' }}>
      <Nav />
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        <Card
          sx={{
            borderRadius: 4,
            border: '1px solid',
            borderColor: alpha(PRIMARYCOLOR, 0.1),
            boxShadow: `0 18px 32px ${alpha(PRIMARYCOLOR, 0.06)}`
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.6 } }}>
            <Stack spacing={2.3}>
              <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ xs: 'flex-start', lg: 'center' }}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIphoneIcon sx={{ color: PRIMARYCOLOR }} />
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
                      Call Review
                    </Typography>
                  </Stack>
                  <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 800, color: '#10233B' }}>
                    Vishing Calls
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.85, maxWidth: 760, color: SLATE_TEXT, lineHeight: 1.75 }}>
                    Review campaign call outcomes, open recordings, and inspect transcripts without leaving the table workflow.
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  label={`${filteredCalls.length} of ${callsData.length || 0} shown`}
                  sx={{
                    fontWeight: 800,
                    bgcolor: alpha(PRIMARYCOLOR, 0.08),
                    color: PRIMARYCOLOR
                  }}
                />
              </Stack>

              <Grid container spacing={2} columns={{ xs: 12, lg: 10 }}>
                {metricCards.map((metric) => (
                  <Grid item xs={12} sm={6} lg={2} key={metric.label}>
                    <MetricCard {...metric} />
                  </Grid>
                ))}
              </Grid>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                {QUICK_FILTERS.map((item) => (
                  <Chip
                    key={item.key}
                    label={item.label}
                    clickable
                    onClick={() => setQuickFilter(item.key)}
                    sx={{
                      fontWeight: 700,
                      borderRadius: 999,
                      bgcolor: quickFilter === item.key ? PRIMARYCOLOR : '#ffffff',
                      color: quickFilter === item.key ? '#ffffff' : PRIMARYCOLOR,
                      border: '1px solid',
                      borderColor: quickFilter === item.key ? PRIMARYCOLOR : alpha(PRIMARYCOLOR, 0.16)
                    }}
                  />
                ))}
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', md: 'center' }}>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 150 } }}>
                  <InputLabel>Filter By</InputLabel>
                  <Select value={searchCategory} label="Filter By" onChange={(event) => setSearchCategory(event.target.value)}>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="phone">Phone</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                    <MenuItem value="result">Passed/Failed</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  placeholder="Search calls"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  sx={{ minWidth: { xs: '100%', md: 260 } }}
                />

                <Button variant="outlined" onClick={() => setSearchTerm('')} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
                  Clear
                </Button>
              </Stack>
            </Stack>
          </CardContent>

          <Divider />

          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: PRIMARYCOLOR,
                      color: '#ffffff',
                      fontWeight: 800,
                      letterSpacing: '0.03em',
                      borderBottom: 'none'
                    }
                  }}
                >
                  <TableCell>User</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Caller ID</TableCell>
                  <TableCell>Redials</TableCell>
                  <TableCell>Scenario</TableCell>
                  <TableCell>Voice</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>Ended Call Reason</TableCell>
                  <TableCell width={120}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 7 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredCalls.length > 0 ? (
                  filteredCalls.map((call) => {
                    const country = getCountryFromPhone(call.phone);
                    const voiceId = call?.voice ? String(call.voice) : null;
                    const scenarioId = call?.scenario ? String(call.scenario) : null;
                    const maxRedials = resolveMaxRedials(call?.max_redials);
                    const voice = voiceId && voiceMap[voiceId] ? voiceMap[voiceId] : null;
                    const scenario = scenarioId && scenarioMap[scenarioId] ? scenarioMap[scenarioId] : null;

                    return (
                      <TableRow
                        key={call.id}
                        hover
                        sx={{
                          '& td': { borderBottomColor: alpha(PRIMARYCOLOR, 0.06) },
                          '&:nth-of-type(odd)': { backgroundColor: alpha(PRIMARYCOLOR, 0.016) },
                          '&:hover': { backgroundColor: alpha(PRIMARYCOLOR, 0.05) }
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(PRIMARYCOLOR, 0.12), color: PRIMARYCOLOR }}>
                              {getInitials(call.name)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: '#10233B' }}>{call.name || 'Unknown user'}</Typography>
                              <Typography sx={cellLabelSX}>ID: {call.id}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                fontSize: '0.78rem',
                                bgcolor: alpha(PRIMARYCOLOR, 0.08),
                                color: PRIMARYCOLOR
                              }}
                            >
                              {country.flag}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600, color: '#20344B' }}>{call.phone || '—'}</Typography>
                              <Typography sx={cellLabelSX}>{country.name}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 600, color: '#20344B', whiteSpace: 'nowrap' }}>
                            {call.last_caller_number || call.caller_number || 'Automatic'}
                          </Typography>
                          <Typography sx={cellLabelSX}>
                            {call.last_caller_number
                              ? 'Last number used'
                              : call.caller_number
                                ? 'Requested number'
                                : 'Round robin'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 600, color: '#20344B', whiteSpace: 'nowrap' }}>
                            {maxRedials} Redial{maxRedials === 1 ? '' : 's'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1.2} alignItems="center">
                            <Avatar src={scenario?.image} alt={scenario?.name} sx={{ width: 32, height: 32 }} />
                            <Box>
                              <Typography sx={{ fontWeight: 600, color: '#20344B' }}>{scenario?.name || '—'}</Typography>
                              <Typography sx={cellLabelSX}>Scenario #{scenarioId || '—'}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1.2} alignItems="center">
                            <Avatar src={voice?.image} alt={voice?.name} sx={{ width: 32, height: 32 }} />
                            <Box>
                              <Typography sx={{ fontWeight: 600, color: '#20344B' }}>{voice?.name || '—'}</Typography>
                              <Typography sx={cellLabelSX}>{voice?.language ? voice.language.toUpperCase() : '—'}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.6} alignItems="flex-start">
                            <Chip
                              size="small"
                              label={(call.status || '—').toUpperCase()}
                              color={statusColor(call.status)}
                              variant="outlined"
                              sx={{ fontWeight: 700 }}
                            />
                            {call?.recording ? (
                              <Chip
                                size="small"
                                label="Audio Available"
                                sx={{
                                  fontWeight: 700,
                                  bgcolor: alpha(PRIMARYCOLOR, 0.08),
                                  color: PRIMARYCOLOR
                                }}
                              />
                            ) : null}
                          </Stack>
                        </TableCell>

                        <TableCell>{passFailChip(call.passOrFail)}</TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: 13, color: SLATE_TEXT, lineHeight: 1.55 }}>
                            {call.ended_reason || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={0.9}>
                            <Tooltip title="Review call">
                              <IconButton
                                onClick={() => handleViewMore(call)}
                                sx={{
                                  bgcolor: alpha(PRIMARYCOLOR, 0.1),
                                  color: PRIMARYCOLOR,
                                  '&:hover': { bgcolor: alpha(PRIMARYCOLOR, 0.16) }
                                }}
                              >
                                <InfoOutlinedIcon />
                              </IconButton>
                            </Tooltip>

                            {call?.recording ? (
                              <Tooltip title="Open recording">
                                <IconButton
                                  onClick={() => handleViewMore(call)}
                                  sx={{
                                    bgcolor: alpha(PASS_GREEN, 0.1),
                                    color: PASS_GREEN,
                                    '&:hover': { bgcolor: alpha(PASS_GREEN, 0.16) }
                                  }}
                                >
                                  <PlayCircleOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 7 }}>
                      <Typography sx={{ fontWeight: 700, color: '#10233B' }}>No calls found.</Typography>
                      <Typography variant="body2" sx={{ mt: 0.6, color: SLATE_TEXT }}>
                        Try a different search term or quick filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      <VishingCallModal open={openModal} onClose={() => setOpenModal(false)} call={selectedCall} />
    </Box>
  );
};

export default VishingCalls;
